import { DefaultTheme } from 'styled-components';
import { Breakpoint, BreakpointMap } from 'types';

const PRIMARY_FONT_FAMILY = 'Roboto, sans-serif';
const SECONDARY_FONT_FAMILY = 'Montserrat, sans-serif';
const MONOSPACE_FONT_FAMILY = '"Courier Prime", monospace';

const BREAKPOINT_STEP = 5;
const DEFAULT_BREAKPOINT_MAP: Record<Breakpoint, number> = {
  sm: 768,
  md: 992,
  lg: 1200,
};

function createTransition(
  properties: string | string[],
  duration: string,
  easing: string,
): string {
  const props = Array.isArray(properties) ? properties : [properties];
  return props.map((prop) => `${prop} ${duration} ${easing}`).join(', ');
}

function createSpacing(
  base: number,
  top: number | string,
  right?: number | string,
  bottom?: number | string,
  left?: number | string,
): string {
  const getSpacingValue = (input: number | string | undefined) => {
    if (typeof input === 'number') {
      return `${base * input}px`;
    }
    return input;
  };

  return [top, right, bottom, left]
    .map((input) => getSpacingValue(input))
    .filter(Boolean)
    .join(' ');
}

function createBreakpointQuery(
  breakpoint: number | Breakpoint,
  breakpointMap: BreakpointMap,
  direction: 'up' | 'down',
) {
  const valuePx =
    typeof breakpoint === 'string' ? breakpointMap[breakpoint] : breakpoint;

  const breakpointDirection = direction === 'up' ? 'min' : 'max';

  return `@media (${breakpointDirection}-width:${
    valuePx - BREAKPOINT_STEP / 100
  }px)`;
}

export function createBreakpoints(
  breakpointMap: BreakpointMap,
): DefaultTheme['breakpoints'] {
  return {
    down: (breakpoint) =>
      createBreakpointQuery(breakpoint, breakpointMap, 'down'),
    up: (breakpoint) => createBreakpointQuery(breakpoint, breakpointMap, 'up'),
    keys: Object.keys(breakpointMap) as Breakpoint[],
    values: breakpointMap,
  };
}

const theme: DefaultTheme = {
  breakpoints: createBreakpoints(DEFAULT_BREAKPOINT_MAP),
  legacyPalette: {
    common: {
      black: '#000',
      white: '#fff',
    },
    danger: {
      main: '#c82828',
      dark: '#c82828',
    },
    dark1: {
      main: '#14191e',
      dark: '#14191e',
    },
    dark3: {
      main: '#282e38',
      dark: '#282e38',
    },
    highlight: {
      main: '#f0f9ff',
      dark: '#d2e4fc',
    },
    neutral1: {
      main: '#8ca0c3',
      dark: '#464e5e',
    },
    // TODO change this to dark2
    neutral2: {
      main: '#323746',
      dark: '#323746',
    },
    neutral3: {
      main: '#dce1eb',
      dark: '#dce1eb',
    },
    // TODO change this neutral2
    neutral4: {
      main: '#646e82',
      dark: '#646e82',
    },
    neutral5: {
      main: '#bec8d2',
      dark: '#bec8d2',
    },

    primary: {
      main: '#19b5fe',
      dark: '#019ce3',
    },
    secondary: {
      main: '#6e6ede',
      dark: '#5555c5',
    },
  },
  palette: {
    common: {
      black: '#000',
      white: '#fff',
    },
    actionPrimary: {
      main: '#19b5fe',
      dark: '#019ce3',
    },
    actionSecondary: {
      main: '#6e6ede',
      dark: '#5555c5',
    },
    actionTertiary: {
      main: '#8ca0c3',
      dark: '#646e82',
    },
    d1: '#14191e',
    d2: '#222831',
    d3: '#2a2f3c',
    d4: '#323746',
    d5: '#646e82',
    danger: '#c82828',
    l1: '#fafafa',
    l2: '#fefefe',
    l3: '#f0f9ff',
    l4: '#dce1eb',
    l5: '#bec8d2',
    s1: '#525a73',
    s2: '#e77ac0',
    s3: '#68d3cf',
    s4: '#f9eb69',
    s5: '#f29e5e',
  },
  spacing: {
    base: 8,
    create: (top, right, bottom, left) =>
      createSpacing(theme.spacing.base, top, right, bottom, left),
    dialogOffset: 75,
  },
  transitions: {
    create: (
      props,
      duration = theme.transitions.duration.default,
      easing = 'ease',
    ) => createTransition(props, duration, easing),
    duration: {
      default: '500ms',
    },
  },
  typography: {
    fontFamily: {
      mono: MONOSPACE_FONT_FAMILY,
      primary: PRIMARY_FONT_FAMILY,
      secondary: SECONDARY_FONT_FAMILY,
    },
    variants: {
      adornment: {
        default: {
          fontFamily: PRIMARY_FONT_FAMILY,
          fontSize: '10px',
          fontWeight: 500,
          letterSpacing: '1px',
          lineHeight: 1,
          textTransform: 'uppercase',
        },
      },
      button: {
        default: {
          fontFamily: PRIMARY_FONT_FAMILY,
          fontSize: '12px',
          fontWeight: 700,
          lineHeight: 1,
          letterSpacing: '2px',
          textTransform: 'uppercase',
        },
      },
      fabLabel: {
        default: {
          fontFamily: PRIMARY_FONT_FAMILY,
          fontSize: '16px',
          fontWeight: 500,
          lineHeight: 1,
          letterSpacing: 'normal',
        },
      },
      h1: {
        default: {
          fontFamily: SECONDARY_FONT_FAMILY,
          fontSize: '24px',
          fontWeight: 700,
          lineHeight: 1.42,
        },
      },
      h2: {
        default: {
          fontFamily: PRIMARY_FONT_FAMILY,
          fontSize: '14px',
          fontWeight: 500,
          letterSpacing: '0.5px',
          lineHeight: 1.5,
        },
      },
      h3: {
        default: {
          fontFamily: SECONDARY_FONT_FAMILY,
          fontSize: '18px',
          fontWeight: 700,
          lineHeight: 1.5,
          letterSpacing: '0.5px',
        },
        mobile: {
          fontFamily: SECONDARY_FONT_FAMILY,
          fontSize: '16px',
          fontWeight: 700,
          lineHeight: 1.31,
          letterSpacing: '0.5px',
        },
      },
      h4: {
        default: {
          fontFamily: PRIMARY_FONT_FAMILY,
          fontSize: '12px',
          fontWeight: 500,
          letterSpacing: '0.5px',
          lineHeight: 1.5,
        },
      },
      h5: {
        default: {
          fontFamily: SECONDARY_FONT_FAMILY,
          fontSize: '16px',
          fontWeight: 700,
          lineHeight: 1.38,
          letterSpacing: '0.5px',
        },
      },
      jumbo: {
        default: {
          fontFamily: SECONDARY_FONT_FAMILY,
          fontSize: '40px',
          fontWeight: 700,
          lineHeight: 1.35,
        },
      },
      label: {
        default: {
          fontFamily: PRIMARY_FONT_FAMILY,
          fontSize: '12px',
          fontWeight: 500,
          lineHeight: 1,
        },
      },
      mono: {
        default: {
          fontFamily: MONOSPACE_FONT_FAMILY,
          fontSize: '12px',
          fontWeight: 'normal',
          lineHeight: 1,
        },
      },
      legal: {
        default: {
          fontFamily: PRIMARY_FONT_FAMILY,
          fontSize: '10px',
          lineHeight: 1.4,
        },
      },
      p: {
        default: {
          fontFamily: PRIMARY_FONT_FAMILY,
          fontSize: '12px',
          fontWeight: 700,
          lineHeight: 1.33,
        },
      },
      tooltip: {
        default: {
          fontFamily: PRIMARY_FONT_FAMILY,
          fontSize: '11px',
          fontWeight: 400,
          lineHeight: 1,
        },
      },
    },
  },
  zIndex: {
    overlay: 100,
    dialog: 100,
  },
};

export default theme;
